import { useState } from 'react'
import { useSelector } from 'react-redux'
import props from '../../redux/props'
import UIForm from '../UI/Form/Form'
import Input from '../UI/Input/Input'
import Dropdown from '../UI/Dropdown/Dropdown'
import Textarea from '../UI/Textarea/Textarea'
import Button from '../UI/Button/Button'
import Loading from '../UI/Loading/Loading'
import useSendForm, { Type } from '../hooks/useSendForm'
import { ASAP, coreHoursSummary, dayNames, dayOrder, slotsFor } from './coreHours'

import solarIcon from '../../assets/image/services/solar/icon.svg'
import waterIcon from '../../assets/image/services/water/icon.svg'
import airIcon from '../../assets/image/services/air/icon.svg'
import oilIcon from '../../assets/image/services/oil/icon.svg'

// Dieselben Piktogramme wie im Hero der Startseite und auf dem Flyer — wer den
// QR-Code gescannt hat, erkennt die vier Bereiche wieder, ohne zu lesen.
const icons: Record<string, string> = {
  solar: solarIcon,
  water: waterIcon,
  air: airIcon,
  oil: oilIcon,
}

// Der Rahmen bekommt die Bereichsfarbe, sobald die Kachel gewählt ist.
const activeBorder: Record<string, string> = {
  solar: 'border-green',
  water: 'border-blue',
  air: 'border-lightBlue',
  oil: 'border-orange',
}

type Section = { key: string; name: string }

type Data = {
  name?: string
  phone?: string
  mail?: string
  section?: string
  day?: string
  slot?: string
  message?: string
}

enum Status {
  DEFAULT = 'DEFAULT',
  SUBMITTING = 'SUBMITTING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

/**
 * Pflicht sind Name, Telefon und das Anliegen — damit kann der Betrieb
 * zurückrufen und weiß, worum es geht. Bereich, Wunschtag, Zeitfenster und
 * E-Mail sind Komfort und bleiben freiwillig.
 */
const Form = () => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const sendForm = useSendForm()
  const [data, setData] = useState<Data>({})
  const [status, setStatus] = useState<Status>(Status.DEFAULT)

  const sections: Section[] = t.quickContact.form.sections
  const slots = slotsFor(data.day)

  const set = (key: string) => (value: string) => setData({ ...data, [key]: value })

  // Ohne Tagangabe gelten die Fenster, die an jedem Werktag in die Kernzeit
  // fallen. Ein Tagwechsel kann das gewählte Fenster ungültig machen (Freitag
  // endet um 13 Uhr) — dann fällt die Auswahl zurück auf leer.
  const setDay = (day: string) => {
    const stillValid = slotsFor(day).some((slot) => slot.value === data.slot)
    setData({ ...data, day, slot: stillValid ? data.slot : '' })
  }

  // Die Kacheln sind keine Radiogruppe: Ein zweiter Klick nimmt die Auswahl
  // wieder zurück, sonst käme man aus einem Fehlgriff nicht mehr heraus.
  const toggleSection = (key: string) => set('section')(data.section === key ? '' : key)

  const selectedSection = () => sections.find((section) => section.key === data.section)

  /** Wunschzeit als Klartext — je nachdem, was überhaupt gewählt wurde. */
  const callWindow = () => {
    const slot = slots.find((entry) => entry.value === data.slot)
    const day = data.day === ASAP ? t.quickContact.form.day.asap : data.day ? dayNames[data.day] : ''
    if (day && slot) return `${day}, ${slot.label}`
    return day || (slot ? slot.label : '')
  }

  const submit = async () => {
    setStatus(Status.SUBMITTING)

    const section = selectedSection()
    const window = callWindow()

    // useSendForm parst die Antwort ungeprueft als JSON. Antwortet das Gateway
    // mit etwas anderem — Timeout, 502, Wartungsseite — fliegt die Exception
    // bis hier hoch. Ohne catch bliebe der Status auf SUBMITTING stehen und der
    // Nutzer sieht ewig den Spinner, statt die Telefonnummer angeboten zu
    // bekommen. Genau auf dieser Seite waere das der teuerste Fehler.
    try {
      // Die Lambda-Vorlage kennt nur name / mail / phone / timeslot / topic /
      // message. Herkunft und Wunschzeit werden deshalb in diese Felder
      // einsortiert, statt das Mail-Template zu aendern. Leere Felder bekommen
      // einen Platzhalter, sonst steht in der Mail eine leere Box.
      const ok = await sendForm(Type.CONTACT, {
        name: data.name,
        phone: data.phone,
        mail: data.mail || t.quickContact.form.mailNoEntry,
        topic: section
          ? `${section.name} — ${t.quickContact.form.mailSource}`
          : t.quickContact.form.mailSource,
        timeslot: window || t.quickContact.form.mailNoEntry,
        message: data.message,
      })
      setStatus(ok ? Status.SUCCESS : Status.ERROR)
    } catch {
      setStatus(Status.ERROR)
    }
  }

  if (status === Status.SUCCESS) {
    const window = callWindow()
    return (
      <div className="bg-white rounded-xl p-6 lg:p-10 text-center">
        <div className="font-bold text-lg lg:text-xl">{t.quickContact.form.status.successTitle}</div>
        <div className="text-sm mt-3">
          {window ? (
            <>
              {t.quickContact.form.status.successPrefix}
              <span className="font-bold"> {window}</span>
              {t.quickContact.form.status.successSuffix}
            </>
          ) : (
            t.quickContact.form.status.successNoWindow
          )}
        </div>
        <div className="text-sm text-gray mt-5">{t.quickContact.form.status.successCall}</div>
        <a href={`tel:${t.generic.company.phone}`} className="font-bold text-lg block mt-1">
          {t.generic.company.phone}
        </a>
      </div>
    )
  }

  return (
    <UIForm onSubmit={submit}>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-bold text-md">{t.quickContact.form.contactTitle}</div>
            <div className="text-sm text-gray">{t.quickContact.form.contactHint}</div>
          </div>
          <Input value={data.name} onChange={set('name')} placeholder={t.generic.name} required />
          <Input
            value={data.phone}
            onChange={set('phone')}
            placeholder={t.generic.phone}
            type="tel"
            required
          />
          <Textarea
            rows={4}
            value={data.message}
            onChange={set('message')}
            placeholder={t.quickContact.form.message}
            required
          />
          <Input
            value={data.mail}
            onChange={set('mail')}
            placeholder={t.quickContact.form.mailOptional}
            type="mail"
          />
        </div>

        <div>
          <div className="font-bold text-md">{t.quickContact.form.sectionTitle}</div>
          <div className="text-sm text-gray">{t.quickContact.form.sectionHint}</div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {sections.map(({ key, name }) => {
              const selected = data.section === key
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => toggleSection(key)}
                  aria-pressed={selected}
                  className={`flex flex-row items-center gap-3 text-left bg-white rounded-xl border-2 p-3 transition-all ${
                    selected ? activeBorder[key] : 'border-lightGray'
                  }`}
                >
                  <img src={icons[key]} alt="" className="h-10 w-10 shrink-0" />
                  <span className="text-xs leading-4">{name}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <div className="font-bold text-md">{t.quickContact.form.callTitle}</div>
            <div className="text-sm text-gray">{t.quickContact.form.callHint}</div>
          </div>
          <div className="text-sm text-gray">{coreHoursSummary()}</div>
          <Dropdown
            value={data.day}
            onChange={setDay}
            emptySelection={t.quickContact.form.dayPlaceholder}
            items={[
              { label: t.quickContact.form.day.asap, value: ASAP },
              ...dayOrder.map((day) => ({ label: dayNames[day], value: day })),
            ]}
          />
          <Dropdown
            value={data.slot}
            onChange={set('slot')}
            emptySelection={t.quickContact.form.slotPlaceholder}
            items={slots.map((slot) => ({ label: slot.label, value: slot.value }))}
          />
          {/* Ohne Tagangabe steht nur die Schnittmenge aller Werktage zur Wahl —
              sonst liesse sich freitags 15 Uhr auswaehlen. */}
          {(!data.day || data.day === ASAP) && (
            <div className="text-xs text-gray">{t.quickContact.form.asapHint}</div>
          )}
        </div>

        {status === Status.ERROR && (
          <div className="text-center font-bold text-orange">{t.contact.form.status.error}</div>
        )}

        {status === Status.SUBMITTING ? (
          <div className="flex flex-col justify-center gap-3">
            <Loading loading={false} />
            <div className="text-center font-bold text-gray">{t.contact.form.status.processing}</div>
          </div>
        ) : (
          <Button type="submit" dark text={t.quickContact.form.cta} className="mx-auto py-2 lg:py-3" />
        )}

        <div className="text-xs text-gray text-center">
          {t.quickContact.form.privacy}{' '}
          <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">
            {t.quickContact.form.privacyLink}
          </a>
        </div>
      </div>
    </UIForm>
  )
}

export default Form
